import React from 'react';
import {
    Card,
    AdvancedTable,
    AdvancedTableData,
    AdvancedTableHeader,
    PaginationData,
} from 'components';
import userPlaceholderIcon from 'components/assets/svg/applicants_icon.svg';
import { formatPhoneNumber, formatSnils } from '_helpers';
import { IApiError } from '_helpers/ApiConnector/types';

const ApplicantList = ({
    getList,
    handleApplicantSelect,
    applicantList,
    apiError,
    processing,
    filters,
    placeholderText,
    placeholderTitle,
    cardCategory,
    cardTitle,
}: Props) => {
    const [listSortOrder, setListSortOrder] = React.useState(true);
    const { pathname } = window.location;

    const handleListOrder = (fieldName: string) => {
        setListSortOrder(!listSortOrder);
        getList({
            ...filters, pathname, sortField: fieldName, sortOrder: listSortOrder,
        });
    };

    const handleListSearch = (fieldName: string, value: string) => {
        getList({
            ...filters, page: 1, pathname, searchField: fieldName, searchTerm: value,
        });
    };

    const handlePageChange = (event: PaginationData) => {
        getList({
            ...filters, pathname, page: event.page, pageSize: event.perPage,
        });
    };

    const listTableHeaders: AdvancedTableHeader[] = [
        { fieldName: 'fullName', title: 'ФАМИЛИЯ, ИМЯ, ОТЧЕСТВО', columnStyles: { width: '70%' } },
        { fieldName: 'email', title: 'ПОЧТА', columnStyles: { width: '30%' } },
        { fieldName: 'snils', title: 'СНИЛС', columnStyles: { minWidth: '10rem' } },
        { fieldName: 'phone', title: 'ТЕЛЕФОН', columnStyles: { minWidth: '10rem' } },
    ];

    const createListTableData = (users: Applicant[]): AdvancedTableData[] => users.map(user => ({
        id: user.id,
        columns: [
            user.fullName || `${user.lastName} ${user.firstName} ${user.middleName}`,
            user.email,
            formatSnils(user.snils),
            formatPhoneNumber(user.phone) || user.phone,
        ],
    }));

    const tableFilters = filters as { [key: string]: string };

    return (
        <Card
          title={cardTitle || 'Список аккредитуемых'}
          category={cardCategory || 'Список всех аккредитуемых, связанных с АПК'}
          apiError={apiError}
          showLoader={processing}
          withoutPadding
        >
                <AdvancedTable
                  data={createListTableData(applicantList ? applicantList.items : [])}
                  tableHeaders={listTableHeaders}
                  handlePageChange={handlePageChange}
                  handleOrder={handleListOrder}
                  handleRowClick={handleApplicantSelect}
                  handleSearch={handleListSearch}
                  pageSize={applicantList ? applicantList.pageSize : 10}
                  total={applicantList ? applicantList.total : 1}
                  page={applicantList ? applicantList.page : 1}
                  placeholderIcon={userPlaceholderIcon}
                  filters={tableFilters}
                  placeholderTitle={placeholderTitle || 'Нет добавленных аккредитуемых'}
                  placeholderText={placeholderText}
                />
        </Card>
    );
};

export default ApplicantList;

interface Props {
    applicantList: IList | null;
    apiError: IApiError | null;
    processing: boolean;
    filters?: IGetList | null;
    cardTitle?: string;
    cardCategory?: string;
    placeholderTitle?: string;
    placeholderText?: string;
    getList: (data: IGetList) => void;
    handleApplicantSelect: (id: string) => void;
}

interface IList {
    items: Applicant[];
    page: number;
    pageSize: number;
    total: number;
}

interface IGetList {
    searchField?: string;
    searchTerm?: string;
    page?: number;
    pageSize?: number;
    sortOrder?: boolean;
    sortField?: string;
    pathname?: string;
}

interface Applicant {
    id: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    fullName?: string;
    email: string;
    phone: string;
    snils: string;
}
